import React, { useState, useEffect, useMemo } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import { EmojiEvents, Refresh } from '@mui/icons-material';
import {
  ResponsiveContainer,
  XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip, ReferenceLine,
  ScatterChart, Scatter, ZAxis,
} from 'recharts';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useYear } from '../context/YearContext';
import PageYearSelector from '../components/common/PageYearSelector';
import { teamsApi } from '../services/apiService';
import { getTeamLogoUrl } from '../utils/teamLogos';
import useMobile from '../hooks/useMobile';

/* ──────────────────── Charts Sub-Component ──────────────────── */

const pythag = (rs, ra, exp = 1.83) => {
  if (!rs || !ra || ra === 0) return 0;
  return Math.round((Math.pow(rs, exp) / (Math.pow(rs, exp) + Math.pow(ra, exp))) * (rs + ra > 0 ? 162 : 0));
};

// Custom tooltip for expected vs actual wins
const PythagTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const diff = d.wins - d.expectedWins;
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 2, boxShadow: 3, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" fontWeight={700}>{d.teamName}</Typography>
      <Typography variant="body2">Actual Wins: <strong>{d.wins}</strong></Typography>
      <Typography variant="body2">Expected Wins: <strong>{d.expectedWins}</strong></Typography>
      <Typography variant="body2" sx={{ color: diff > 0 ? '#2e7d32' : diff < 0 ? '#c62828' : 'text.secondary' }}>
        {diff > 0 ? 'Lucky' : diff < 0 ? 'Unlucky' : 'Neutral'}: {diff > 0 ? '+' : ''}{diff} wins
      </Typography>
    </Box>
  );
};

const StandingsCharts = ({ standings, selectedLeague, theme, isMobile, navigate }) => {
  // Flatten all teams
  const allTeams = useMemo(() => {
    if (!standings?.divisions) return [];
    const teams = [];
    Object.values(standings.divisions).forEach(divTeams => {
      divTeams.forEach(t => {
        if (selectedLeague === 'all' ||
            (selectedLeague === 'al' && t.league === 'American League') ||
            (selectedLeague === 'nl' && t.league === 'National League')) {
          teams.push(t);
        }
      });
    });
    return teams;
  }, [standings, selectedLeague]);

  // Pythagorean expected wins data
  const pythagData = useMemo(() =>
    allTeams.map(t => ({
      ...t,
      expectedWins: pythag(t.runsScored, t.runsAllowed),
      luck: t.wins - pythag(t.runsScored, t.runsAllowed),
    })).sort((a, b) => Math.abs(b.luck) - Math.abs(a.luck)),
    [allTeams]
  );

  if (!allTeams.length) return <Alert severity="info">No data available for charts.</Alert>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Pythagorean Expected vs Actual Wins */}
      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
          color: 'white', py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: isMobile ? '1rem' : undefined }}>
            Expected vs Actual Wins (Pythagorean)
          </Typography>
        </Box>
        <CardContent sx={{ p: isMobile ? 1 : 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Teams above the line are "lucky" (outperforming run differential), teams below are "unlucky."
          </Typography>
          <ResponsiveContainer width="100%" height={isMobile ? 320 : 420}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="expectedWins"
                name="Expected Wins"
                tick={{ fontSize: 12 }}
                label={{ value: 'Expected Wins', position: 'bottom', fontSize: 13, fontWeight: 600 }}
              />
              <YAxis
                type="number"
                dataKey="wins"
                name="Actual Wins"
                tick={{ fontSize: 12 }}
                label={{ value: 'Actual Wins', angle: -90, position: 'insideLeft', fontSize: 13, fontWeight: 600 }}
              />
              <ZAxis range={[isMobile ? 80 : 120]} />
              <RTooltip content={<PythagTooltip />} />
              {/* Perfect correlation line */}
              <ReferenceLine
                segment={[
                  { x: Math.min(...pythagData.map(d => d.expectedWins)) - 3, y: Math.min(...pythagData.map(d => d.expectedWins)) - 3 },
                  { x: Math.max(...pythagData.map(d => d.expectedWins)) + 3, y: Math.max(...pythagData.map(d => d.expectedWins)) + 3 },
                ]}
                stroke={theme.palette.divider}
                strokeWidth={2}
                strokeDasharray="6 3"
              />
              <Scatter
                data={pythagData}
                shape={(props) => {
                  const { cx, cy, payload } = props;
                  const size = isMobile ? 24 : 32;
                  return (
                    <image
                      x={cx - size / 2}
                      y={cy - size / 2}
                      width={size}
                      height={size}
                      href={getTeamLogoUrl(payload.teamCode)}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/teams/${payload.teamCode}`)}
                    />
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Luck Table */}
      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.9)}, ${theme.palette.warning.dark})`,
          color: 'white', py: 1.5, px: 3,
        }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: isMobile ? '1rem' : undefined }}>
            Luck Rankings
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.warning.main, 0.04) }}>
                <TableCell sx={{ fontWeight: 700, minWidth: isMobile ? 90 : 160 }}>Team</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>W</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>xW</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Luck</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>RS</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>RA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pythagData.map((team, idx) => (
                <TableRow
                  key={team.teamCode}
                  onClick={() => navigate(`/teams/${team.teamCode}`)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={getTeamLogoUrl(team.teamCode)} sx={{ width: 22, height: 22, bgcolor: 'grey.200' }}
                        imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>{team.teamCode?.[0]}</Avatar>
                      {!isMobile && <Typography variant="body2" fontWeight={500}>{team.teamName}</Typography>}
                      <Chip label={team.teamCode} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                    </Box>
                  </TableCell>
                  <TableCell align="center"><Typography variant="body2" fontWeight={600}>{team.wins}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2">{team.expectedWins}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${team.luck > 0 ? '+' : ''}${team.luck}`}
                      size="small"
                      sx={{
                        fontWeight: 700, fontSize: '0.75rem', height: 22,
                        bgcolor: team.luck > 2 ? alpha('#2e7d32', 0.12) : team.luck < -2 ? alpha('#c62828', 0.12) : alpha(theme.palette.text.secondary, 0.08),
                        color: team.luck > 2 ? '#2e7d32' : team.luck < -2 ? '#c62828' : 'text.secondary',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center"><Typography variant="body2">{team.runsScored}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2">{team.runsAllowed}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

/* ──────────────────── Main Component ──────────────────── */

const Standings = () => {
  const theme = useTheme();
  const { year } = useYear();
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState('divisions');
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [gameType, setGameType] = useState('R');

  useEffect(() => {
    fetchStandings();
  }, [year, gameType]);

  const fetchStandings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await teamsApi.getStandings({ year, gameType });
      setStandings(data);
    } catch (err) {
      // For postseason or spring training, treat errors as "no data" rather than showing error
      if (gameType !== 'R') {
        setStandings(null);
        setError(null);
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to fetch standings');
      }
    } finally {
      setLoading(false);
    }
  };

  const getDivisionsByLeague = (league) => {
    if (!standings?.divisions) return {};
    if (league === 'all') return standings.divisions;
    const filtered = {};
    Object.entries(standings.divisions).forEach(([divName, teams]) => {
      if (teams.length > 0) {
        const teamLeague = teams[0].league;
        if ((league === 'al' && teamLeague === 'American League') ||
            (league === 'nl' && teamLeague === 'National League')) {
          filtered[divName] = teams;
        }
      }
    });
    return filtered;
  };

  // Check if extended stats (home/away/L10/streak) have meaningful data
  const hasExtendedStats = () => {
    if (!standings?.divisions) return false;
    return Object.values(standings.divisions).some(teams =>
      teams.some(t => t.homeRecord !== '0-0' || t.awayRecord !== '0-0' || t.lastTen !== '0-0')
    );
  };

  const renderDivisionTable = (divisionName, teams) => {
    const showExtended = hasExtendedStats();
    return (
      <Card key={divisionName} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
          py: isMobile ? 1 : 1.5,
          px: isMobile ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <EmojiEvents sx={{ fontSize: 20 }} />
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: isMobile ? '1rem' : undefined }}>{divisionName}</Typography>
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                <TableCell sx={{ fontWeight: 700, minWidth: isMobile ? 100 : 180 }}>Team</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>W</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>L</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>PCT</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>GB</TableCell>
                {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>RS</TableCell>}
                {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>RA</TableCell>}
                <TableCell align="center" sx={{ fontWeight: 700 }}>DIFF</TableCell>
                {showExtended && !isMobile && (
                  <>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>L10</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>HOME</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>AWAY</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {teams.map((team, index) => (
                <TableRow
                  key={team.teamCode}
                  onClick={() => navigate(`/teams/${team.teamCode}`)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: index === 0 ? alpha(theme.palette.success.main, 0.06) : 'transparent',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                    transition: 'background-color 0.15s'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={getTeamLogoUrl(team.teamCode)}
                        sx={{ width: 24, height: 24, bgcolor: 'grey.200' }}
                        imgProps={{ style: { objectFit: 'contain', background: 'white' } }}
                      >
                        {team.teamCode?.[0]}
                      </Avatar>
                      {!isMobile && (
                        <Typography variant="body2" fontWeight={index === 0 ? 700 : 500}>
                          {team.teamName}
                        </Typography>
                      )}
                      <Chip label={team.teamCode} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.75rem' }} />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>{team.wins}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{team.losses}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>
                      {typeof team.winPct === 'number' ? team.winPct.toFixed(3) : '---'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ color: team.gamesBehind === '—' ? 'success.main' : 'text.secondary' }}>
                      {team.gamesBehind}
                    </Typography>
                  </TableCell>
                  {!isMobile && <TableCell align="center"><Typography variant="body2">{team.runsScored}</Typography></TableCell>}
                  {!isMobile && <TableCell align="center"><Typography variant="body2">{team.runsAllowed}</Typography></TableCell>}
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600} sx={{
                      color: team.runDiff > 0 ? 'success.main' : team.runDiff < 0 ? 'error.main' : 'text.secondary'
                    }}>
                      {team.runDiff > 0 ? '+' : ''}{team.runDiff}
                    </Typography>
                  </TableCell>
                  {showExtended && !isMobile && (
                    <>
                      <TableCell align="center"><Typography variant="body2">{team.lastTen}</Typography></TableCell>
                      <TableCell align="center"><Typography variant="body2">{team.homeRecord}</Typography></TableCell>
                      <TableCell align="center"><Typography variant="body2">{team.awayRecord}</Typography></TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

  const renderWildCardTable = (league, teams) => (
    <Card key={league} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
        color: 'white',
        py: isMobile ? 1 : 1.5,
        px: isMobile ? 2 : 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <EmojiEvents sx={{ fontSize: 20 }} />
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: isMobile ? '1rem' : undefined }}>{league} Wild Card</Typography>
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.04) }}>
              <TableCell sx={{ fontWeight: 700, minWidth: isMobile ? 100 : 180 }}>Team</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>W</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>L</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>PCT</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>WCGB</TableCell>
              {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>Division</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.slice(0, 8).map((team, index) => (
              <TableRow
                key={team.teamCode}
                onClick={() => navigate(`/teams/${team.teamCode}`)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: index < 3 ? alpha(theme.palette.success.main, 0.06) : 'transparent',
                  '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08) },
                  transition: 'background-color 0.15s'
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={getTeamLogoUrl(team.teamCode)}
                      sx={{ width: 24, height: 24, bgcolor: 'grey.200' }}
                      imgProps={{ style: { objectFit: 'contain', background: 'white' } }}
                    >
                      {team.teamCode?.[0]}
                    </Avatar>
                    {!isMobile && (
                      <Typography variant="body2" fontWeight={index < 3 ? 700 : 500}>
                        {team.teamName}
                      </Typography>
                    )}
                    <Chip label={team.teamCode} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.75rem' }} />
                  </Box>
                </TableCell>
                <TableCell align="center"><Typography variant="body2" fontWeight={600}>{team.wins}</Typography></TableCell>
                <TableCell align="center"><Typography variant="body2">{team.losses}</Typography></TableCell>
                <TableCell align="center"><Typography variant="body2" fontWeight={600}>{typeof team.winPct === 'number' ? team.winPct.toFixed(3) : '---'}</Typography></TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ color: team.wildCardGB === '—' ? 'success.main' : 'text.secondary' }}>
                    {team.wildCardGB || '—'}
                  </Typography>
                </TableCell>
                {!isMobile && (
                <TableCell align="center">
                  <Typography variant="body2" color="text.secondary">{team.division}</Typography>
                </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="caption" color="text.secondary">
          🟢 Green = Playoff position (top 3 Wild Card spots)
        </Typography>
      </Box>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={40} />
        <Typography color="text.secondary">Loading standings...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={fetchStandings} startIcon={<Refresh />}>
          Try Again
        </Button>
      </Box>
    );
  }

  if (!standings) {
    const gameTypeLabel = gameType === 'P' ? 'Postseason' : gameType === 'S' ? 'Spring Training' : 'Regular Season';
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <PageYearSelector />
            </Box>
          </Box>

          {/* Controls */}
          <Card sx={{ mb: 3, p: 2, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
              <Box>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Season Type
                </Typography>
                <ToggleButtonGroup
                  value={gameType}
                  exclusive
                  onChange={(e, v) => v && setGameType(v)}
                  size="small"
                >
                  <ToggleButton value="R">🏟️ Regular</ToggleButton>
                  <ToggleButton value="S">🌴 Spring</ToggleButton>
                  <ToggleButton value="P">🏆 Postseason</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Card>

          <Alert severity="info">No {gameTypeLabel.toLowerCase()} standings data available for {year}.</Alert>
        </motion.div>
      </Box>
    );
  }

  const divisions = getDivisionsByLeague(selectedLeague);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1400, mx: 'auto', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
            <PageYearSelector />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {standings.gameTypeLabel || (gameType === 'R' ? 'Regular Season' : gameType === 'S' ? 'Spring Training' : 'Postseason')} · {standings.totalTeams} teams
          </Typography>
        </Box>

        {/* Controls */}
        <Card sx={{ mb: 3, p: 2, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
            {/* View Toggle */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                View
              </Typography>
              <ToggleButtonGroup
                value={selectedView}
                exclusive
                onChange={(e, v) => v && setSelectedView(v)}
                size="small"
              >
                <ToggleButton value="divisions">Divisions</ToggleButton>
                <ToggleButton value="wildcard">Wild Card</ToggleButton>
                <ToggleButton value="charts">📊 Analytics</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* League Filter */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                League
              </Typography>
              <ToggleButtonGroup
                value={selectedLeague}
                exclusive
                onChange={(e, v) => v && setSelectedLeague(v)}
                size="small"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="al">AL</ToggleButton>
                <ToggleButton value="nl">NL</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Game Type */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Season Type
              </Typography>
              <ToggleButtonGroup
                value={gameType}
                exclusive
                onChange={(e, v) => v && setGameType(v)}
                size="small"
              >
                <ToggleButton value="R">🏟️ Regular</ToggleButton>
                <ToggleButton value="S">🌴 Spring</ToggleButton>
                <ToggleButton value="P">🏆 Postseason</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Card>

        {/* Division View */}
        {selectedView === 'divisions' && (
          <Box>
            {Object.keys(divisions).length > 0 ? (
              Object.entries(divisions).map(([divName, teams]) =>
                renderDivisionTable(divName, teams)
              )
            ) : (
              <Alert severity="info">No division data available for this selection.</Alert>
            )}
          </Box>
        )}

        {/* Wild Card View */}
        {selectedView === 'wildcard' && standings.wildCard && (
          <Box>
            {(selectedLeague === 'all' || selectedLeague === 'al') &&
              renderWildCardTable('American League', standings.wildCard['American League'] || [])
            }
            {(selectedLeague === 'all' || selectedLeague === 'nl') &&
              renderWildCardTable('National League', standings.wildCard['National League'] || [])
            }
          </Box>
        )}

        {/* Charts View */}
        {selectedView === 'charts' && standings?.divisions && (
          <StandingsCharts standings={standings} selectedLeague={selectedLeague} theme={theme} isMobile={isMobile} navigate={navigate} />
        )}

        {/* Legend */}
        <Card sx={{ mt: 3, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Legend</Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>W/L:</strong> Wins/Losses · <strong>PCT:</strong> Win Percentage · <strong>GB:</strong> Games Behind · <strong>RS/RA:</strong> Runs Scored/Allowed · <strong>DIFF:</strong> Run Differential
              {hasExtendedStats() && ' · L10: Last 10 Games · HOME/AWAY: Home/Away Record'}
            </Typography>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default Standings;
